import { Box } from '@mui/material'
import { useCallback } from 'react'

import useIsEmbeddedChat from '~/features/chatbot/hooks/useIsEmbeddedChat'
import Dialog from '~/features/chatbotV2/components/dialog'
import Header from '~/features/chatbotV2/components/simple/header'
import useChatAreaReady from '~/features/chatbotV2/hooks/useChatAreaReady'
import useChatV2 from '~/features/chatbotV2/hooks/useChatV2'
import useTurnstileToken from '~/features/chatbotV2/hooks/useTurnstileToken'

// Clone of features/chatbot/components/simple (v1). useChatV2 is called
// here (once, per WebSocket connection) rather than inside DialogInput —
// v1 can rely on react-query's global useIsMutating to observe pending state
// from anywhere in the tree, but v2's hook has no equivalent global
// observability, so mutate/isPending/endConversation are threaded down as
// props instead. Header is v2's own clone (not v1's, unlike LinkView's
// header/intro which stay shared) — its close/minimize/links UI and
// end-conversation wiring are v2-specific, see that component's own
// comment for why.
function Simple() {
  const { isEmbedded, isMinimized } = useIsEmbeddedChat()
  const showChats = !isEmbedded || !isMinimized
  const turnstile = useTurnstileToken()
  const {
    mutate,
    isPending,
    hasSessionEnded,
    endConversation,
    restartConversation,
    guestToken,
    refreshGuestToken,
    isSessionReady,
    isAiBusy,
    sendTyping,
  } = useChatV2(turnstile)

  const { isWaitingForChatReady, resetChatAreaReady } = useChatAreaReady({
    guestToken,
    isSessionReady,
    refreshGuestToken,
  })

  const handleRestartConversation = useCallback(() => {
    resetChatAreaReady()
    return restartConversation()
  }, [resetChatAreaReady, restartConversation])

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {turnstile.widget}
      <Header
        endConversation={endConversation}
        restartConversation={handleRestartConversation}
        isAiBusy={isAiBusy}
        hasSessionEnded={hasSessionEnded}
      />
      {showChats && (
        <Dialog
          mutate={mutate}
          isPending={isPending}
          hasSessionEnded={hasSessionEnded}
          guestToken={guestToken}
          refreshGuestToken={refreshGuestToken}
          isSessionReady={isSessionReady}
          isWaitingForChatReady={isWaitingForChatReady}
          sendTyping={sendTyping}
        />
      )}
    </Box>
  )
}

export default Simple
